import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Plus, Trash2, Users } from "lucide-react";
import { api, formatApiError } from "../../../api";
import { DESK } from "../../../constants/testIds";
import { useDeskData } from "../../../contexts/DeskDataContext";
import { fadeUp } from "../../../lib/motion";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { toast } from "../../../components/ui/sonner";
import { GymLoader } from "../../../components/common/GymLoader";
import { FormField } from "../../../components/common/FormField";
import { SubmitButton } from "../../../components/common/SubmitButton";
import { useSuccessPulse } from "../../../hooks/useSuccessPulse";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

// UX-FD-03 §1 SCR-P02-85: "shift-anchoring scope only (no payroll-grade HR)" — on-shift branch
// staff is a real, live read (`GET /shifts/current`); trainer scheduling reuses Prompt 04's real
// `availability` windows verbatim. No staff/provider DIRECTORY endpoint exists anywhere on this
// backend (confirmed: `/staff` is Super-Admin-only, and there's no `/providers` list) — a
// trainer's reference ID is entered directly, the same honest "manual ref, no fabricated
// lookup" pattern MoneyIn.jsx already uses for mandate provider refs.
export default function Roster() {
  const { branchId } = useDeskData();
  const reducedMotion = useReducedMotion();
  const [providerRef, setProviderRef] = useState("");
  const [loadedRef, setLoadedRef] = useState(null);
  const [windows, setWindows] = useState([]);
  const [day, setDay] = useState("mon");
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("18:00");
  const [saveSuccess, savePulse] = useSuccessPulse();

  const onShiftQ = useQuery({
    queryKey: ["desk-mgr-on-shift", branchId],
    queryFn: async () => (await api.get("/shifts/current", { params: { branch_id: branchId } })).data.on_shift_staff_refs,
    enabled: !!branchId,
  });

  const availQ = useQuery({
    queryKey: ["desk-mgr-availability", loadedRef],
    queryFn: async () => {
      try {
        return (await api.get(`/availability/${loadedRef}`)).data;
      } catch (e) {
        if (e.response?.status === 404) return { provider_ref: loadedRef, windows: [], exceptions: [] };
        throw e;
      }
    },
    enabled: !!loadedRef,
  });

  useEffect(() => { if (availQ.data) setWindows(availQ.data.windows); }, [availQ.data]);

  const saveMutation = useMutation({
    mutationFn: () => api.post("/availability", { provider_ref: loadedRef, windows, exceptions: [] }),
    onSuccess: () => savePulse(() => toast.success("Availability saved.")),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const load = () => {
    const ref = providerRef.trim();
    if (!ref) return;
    setLoadedRef(ref);
  };

  return (
    <div data-testid={DESK.mgrRosterPage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Roster</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Who's on shift right now, plus trainer availability windows.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.03)} className="rounded-[14px] p-5" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <p className="flex items-center gap-1.5 text-sm font-semibold mb-2" style={{ color: "#141416" }}><Users size={15} /> On shift now</p>
        <div data-testid={DESK.mgrRosterOnShiftList}>
          {(onShiftQ.data || []).length === 0 && <p className="text-sm" style={{ color: "#68686E" }}>Nobody clocked in at this branch right now.</p>}
          {(onShiftQ.data || []).map((ref) => <p key={ref} className="text-sm font-mono" style={{ color: "#141416" }}>{ref}</p>)}
        </div>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.06)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <p className="text-sm font-semibold" style={{ color: "#141416" }}>Trainer availability</p>
        <div className="flex gap-2 items-end">
          <FormField label="Trainer/provider reference ID" required testId={DESK.mgrRosterProviderInput} placeholder="Trainer/provider reference ID" value={providerRef} onChange={setProviderRef} className="flex-1" />
          <Button data-testid={DESK.mgrRosterLoadButton} onClick={load} disabled={!providerRef.trim()} className="shrink-0" variant="outline">Load</Button>
        </div>

        {availQ.isLoading && <GymLoader label="Loading availability…" testId="desk-mgr-roster-loader" />}

        {availQ.data && (
          <>
            {windows.map((w, i) => (
              <div key={i} data-testid={DESK.mgrRosterWindowRow(i)} className="flex items-center justify-between text-sm rounded-[8px] px-3 py-2" style={{ background: "#F4F3EF" }}>
                <span style={{ color: "#141416" }}>{w.day.toUpperCase()}: {w.start}–{w.end}</span>
                <button onClick={() => setWindows(windows.filter((_, idx) => idx !== i))}><Trash2 size={13} style={{ color: "#68686E" }} /></button>
              </div>
            ))}
            <div className="flex gap-2 items-end">
              <Select onValueChange={setDay} value={day}>
                <SelectTrigger data-testid={DESK.mgrRosterDayInput}><SelectValue /></SelectTrigger>
                <SelectContent>{DAYS.map((d) => <SelectItem key={d} value={d}>{d.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid={DESK.mgrRosterStartInput} type="time" value={start} onChange={(e) => setStart(e.target.value)} />
              <Input data-testid={DESK.mgrRosterEndInput} type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
              <Button data-testid={DESK.mgrRosterAddWindowButton} variant="outline" onClick={() => setWindows((prev) => [...prev, { day, start, end }])}><Plus size={14} /></Button>
            </div>
            <SubmitButton
              testId={DESK.mgrRosterSaveButton}
              pending={saveMutation.isPending}
              success={saveSuccess}
              disabled={false}
              onClick={() => saveMutation.mutate()}
              fullWidth
              label="Save availability"
              successLabel="Saved"
            />
          </>
        )}
      </motion.div>
    </div>
  );
}
